import { queryOptions, useQuery } from "@tanstack/react-query"
import {
  BRACKY_CONTRACT_ADDRESS,
  type BrackyPrice,
  GECKO_API_URL,
  REFRESH_INTERVAL,
  STALE_TIME,
} from "../../data/bracky-token-config"

const BASESCAN_API_KEY = import.meta.env.VITE_BASESCAN_API_KEY as string | undefined
const HOLDER_COUNT_URL = BASESCAN_API_KEY
  ? `https://api.basescan.org/api?module=token&action=tokenholdercount&contractaddress=${BRACKY_CONTRACT_ADDRESS}&apikey=${BASESCAN_API_KEY}`
  : null

function extractPrice(attr: Record<string, unknown>) {
  return (attr.base_token_price_usd ??
    attr.token0_price_usd ??
    attr.quote_token_price_usd ??
    attr.token1_price_usd ??
    attr.price_usd) as string | undefined
}

function extractChange24h(attr: Record<string, unknown>) {
  const nested = (attr.price_change_percentage as Record<string, unknown> | undefined)?.h24 as string | undefined
  const flat = (attr.price_change_percentage_24h ?? attr.price_change_24h ?? attr.price_change) as string | undefined
  return nested ?? flat
}

function extractMarketCap(attr: Record<string, unknown>) {
  return (attr.market_cap_usd ?? attr.fdv_usd) as string | undefined
}

function extractHolders(attr: Record<string, unknown>) {
  return (attr.holders ?? attr.holder_count ?? attr.unique_holders ?? attr.holders_count) as string | number | undefined
}

async function fetchHolderCount() {
  if (!HOLDER_COUNT_URL) return 0

  try {
    const res = await fetch(HOLDER_COUNT_URL)
    if (!res.ok) return 0

    const json = await res.json()
    const raw = json?.result as string | undefined

    if (raw && !Number.isNaN(Number(raw))) return Number(raw)
  } catch {
    // Silently fail - holder count is optional
  }

  return 0
}

async function fetchBrackyPrice(): Promise<BrackyPrice> {
  const res = await fetch(GECKO_API_URL)
  if (!res.ok) throw new Error("Failed to fetch BRACKY price")

  const json = await res.json()
  const attr = json?.data?.attributes ?? {}

  const rawPrice = extractPrice(attr)
  const rawChange = extractChange24h(attr)
  const rawMarketCap = extractMarketCap(attr)
  const rawHolders = extractHolders(attr)

  const holdersCount = await fetchHolderCount()

  return {
    priceUsd: rawPrice ? Number.parseFloat(rawPrice) : 0,
    change24h: rawChange ? Number.parseFloat(rawChange) : 0,
    marketCapUsd: rawMarketCap ? Number.parseFloat(rawMarketCap) : 0,
    holders: holdersCount || (rawHolders ? Number(rawHolders.toString().replace(/[, _]/g, "")) : 0),
  }
}

function brackyPriceQueryOptions() {
  return queryOptions({
    queryKey: ["bracky-price"],
    queryFn: fetchBrackyPrice,
    refetchInterval: REFRESH_INTERVAL,
    staleTime: STALE_TIME,
  })
}

export function LandinguseBrackyPrice() {
  return useQuery(brackyPriceQueryOptions())
}
